import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
} from '@app/components/button';
import { DashedContentComponent } from '@app/components/dashed-content';
import { DialogComponent } from '@app/components/dialog';
import {
  InputDirection,
  InputSize,
  InputType,
  InputWidth,
} from '@app/components/input';
import {
  TextAlign,
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextWeight,
} from '@app/components/text';
import { TextInputComponent } from '@app/components/text-input';
import {
  ViewAlign,
  ViewComponent,
  ViewDirection,
  ViewJustify,
} from '@app/components/view';
import { AdminInviteUserService } from '@app/services/admin-invite-user/admin-invite-user.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { createEmailRegex } from '@app/utils';
import { AcceptedDomainsService } from '@app/utils/accepted-domains/accepted-domains.service';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, forkJoin, of, Subscription, tap } from 'rxjs';

enum InviteUserResultStatus {
  SUCCESS = 'success',
  FAIL = 'fail',
  PENDING = 'pending',
}

interface InviteUserResult {
  email: string;
  status: InviteUserResultStatus;
}
@Component({
  standalone: true,
  selector: 'app-invite-users',
  templateUrl: './invite-users.component.html',
  styleUrl: './invite-users.component.css',
  imports: [
    CommonModule,
    TranslatePipe,
    TextInputComponent,
    DialogComponent,
    TextComponent,
    ViewComponent,
    DashedContentComponent,
    ButtonComponent,
  ],
})
export class InviteUsersComponent implements OnDestroy, OnInit {
  /**
   * Services
   */
  private readonly adminInviteService = inject(AdminInviteUserService);
  private readonly acceptedDomainsService = inject(AcceptedDomainsService);
  private readonly translatorService = inject(TranslatorService);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly subscriptions = new Subscription();

  /**
   * State
   */
  isOpen = model(false);
  emailCSVList = signal('');
  showInviteMore = signal(true);
  inviteUserResults = signal<InviteUserResult[]>([]);
  successfullInvites = signal(0);
  failedInvites = signal(0);
  // This is computed on every change! We can improve this.
  emailCSVListError = computed(() => {
    const emailList = this.emailCSVList().split(',');
    const emailListArray = emailList.map((email) => email.trim());
    const emailListValid = emailListArray.every((email) =>
      this.emailRegex?.test(email)
    );
    return !!(this.emailCSVList().length > 0 && !emailListValid);
  });
  submitDisabled = computed(() => {
    return this.emailCSVList().length === 0 || this.emailCSVListError();
  });
  successTitle = computed(() => {
    let title: string;
    if (this.successfullInvites() === 1) {
      title = this.translatorService.translate(
        'users.singleInviteTitleSuccess'
      );
    } else {
      title = this.translatorService.translate('users.inviteTitleSuccesses', {
        successfullInvites: this.successfullInvites(),
      });
    }
    return title;
  });
  failTitle = computed(() => {
    let title: string;
    if (this.failedInvites() === 1) {
      title = this.translatorService.translate('users.singleInviteTitleFailed');
    } else {
      title = this.translatorService.translate('users.inviteTitleFails', {
        failedInvites: this.failedInvites(),
      });
    }
    return title;
  });

  /**
   * Enums
   */
  acceptedDomains: string;
  emailRegex: RegExp;
  isEmailRegexWildcard = false;
  ViewJustify = ViewJustify;
  ViewAlign = ViewAlign;
  ViewDirection = ViewDirection;
  ButtonType = ButtonType;
  ButtonSize = ButtonSize;
  InputType = InputType;
  InputSize = InputSize;
  InputDirection = InputDirection;
  InputWidth = InputWidth;
  Spacing = Spacing;
  TextSize = TextSize;
  TextHeight = TextHeight;
  TextWeight = TextWeight;
  TextColor = TextColor;
  TextAlign = TextAlign;
  InviteUserResultStatus = InviteUserResultStatus;

  constructor() {
    effect(() => {
      const currentlyOpen = this.isOpen();
      untracked(() => {
        if (!currentlyOpen) {
          this.clearDialog();
          this.showInviteMore.set(true);
        }
      });
    });
  }

  /**
   * Life cycles
   */
  ngOnInit() {
    this.subscriptions.add(
      this.adminTenantService.tenant$.subscribe((tenant) => {
        this.acceptedDomains = this.acceptedDomainsService.acceptedDomainsText(
          tenant?.config?.emailDomains?.value
        );
        this.emailRegex = createEmailRegex(tenant?.config?.emailDomains?.value);

        if (tenant.config?.emailDomains?.value[0] === '.*') {
          this.isEmailRegexWildcard = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Handlers and internal methods
   */
  handleDone(): void {
    this.isOpen.set(false);
    this.clearDialog();
    this.showInviteMore.set(!this.showInviteMore());
  }

  handleInviteMore(): void {
    this.showInviteMore.set(!this.showInviteMore());
    this.clearDialog();
  }

  clearDialog(): void {
    this.emailCSVList.set('');
    this.successfullInvites.set(0);
    this.failedInvites.set(0);
    this.inviteUserResults.set([]);
  }

  handleInviteSubmit(): void {
    const tenantId = this.adminTenantService.getTenantId();
    let successes = 0;
    let fails = 0;

    const sanitizedInviteList = [
      ...new Set(
        this.emailCSVList()
          .split(',')
          .map((email) => email.trim())
      ),
    ].map((email) => ({
      email,
      status: InviteUserResultStatus.PENDING,
    }));
    const inviteObservables = sanitizedInviteList?.map((invite) =>
      this.adminInviteService.inviteUser(invite.email, tenantId, false).pipe(
        tap({
          next: () => {
            this.inviteUserItem({
              email: invite.email,
              status: InviteUserResultStatus.SUCCESS,
            });
            successes++;
          },
          error: (error) => {
            console.error(error);
            this.inviteUserItem({
              email: invite.email,
              status: InviteUserResultStatus.FAIL,
            });
            fails++;
          },
        }),
        catchError(() => {
          return of(null);
        })
      )
    );
    forkJoin(inviteObservables).subscribe({
      next: () => {
        this.showInviteMore.set(!this.showInviteMore());
        this.successfullInvites.set(successes);
        this.failedInvites.set(fails);
      },
      error: (error) => {
        console.error('Error in processing invites:', error);
      },
    });
  }

  private inviteUserItem(item: InviteUserResult) {
    this.inviteUserResults.update((results) => {
      return [...results, item];
    });
  }
}
